// src/autoListView.js — generic, form-AGNOSTIC fallback view for a LIST payload (many submissions),
// used when no per-form list view is registered. Mirrors autoView.js but aggregates across submissions.
// All aggregation is client-side via the list helper (d.count/d.aggregate/d.numericColumn/d.geoAll).

export function createAutoListView(html, C) {
  const { Hero, Section, KPI, RepeatedTable, TrendChart, Map, LabTable, Callout, StatGrid } = C;

  return function autoListView(d) {
    const count = d.count();
    const fm = d.filterMeta() || {};
    const total = fm.totalMatched != null ? fm.totalMatched : count;
    const subtitle = total > count
      ? `Top ${count} of ${total} matching submissions`
      : `${count} submission${count === 1 ? '' : 's'}`;

    const subs = d.submissions();
    const firstItem = subs[0];
    const numKeys = d.detectNumericKeys(4);

    // KPI row: average of each detected numeric field across all submissions.
    const kpis = numKeys.map((k) => ({
      key: k,
      label: `Avg ${firstItem ? firstItem.label(k) : k}`,
      value: roundish(d.aggregate(k, 'avg'))
    }));

    // Submissions table: adapt each submission to a row carrying .h (its own helper), so RepeatedTable's
    // get(k) = e.h.text(k) works unchanged.
    const rows = subs.map((it, i) => ({ h: it, instanceIndex: i, fields: it.fields() }));
    const cols = pickColumns(subs, numKeys);

    // Trend of the strongest numeric column across submissions. The API returns newest-first, so reverse
    // to read left→right as oldest→newest.
    const trendKey = numKeys[0];
    const trendPoints = trendKey ? d.numericColumn(trendKey).slice().reverse() : [];

    const geo = d.geoAll();
    const center = centroid(geo);
    const labs = d.labResultsAll();

    // Summary header: what am I looking at + how is it distributed. Status distribution is cheap (a prefixed
    // meta column) and rendered as a NEUTRAL StatGrid (counts only — toning the tiles would be a noisy rainbow).
    const statusDist = count ? countBy(d.textColumn('meta.status')) : [];
    const appliedAt = fm.appliedAt ? formatDay(fm.appliedAt) : '';

    return html`<div class="aq-doc aq-list">
      <${Hero} title=${d.formName} subtitle=${subtitle} meta=${{}} />

      ${count
        ? html`<${Callout} title=${`Showing ${count} of ${total} submission${total === 1 ? '' : 's'}`}>
            ${appliedAt ? `Filtered ${appliedAt}` : null}
          <//>`
        : null}

      ${statusDist.length > 1
        ? html`<${StatGrid} items=${statusDist.map((s) => ({ label: humanize(s.label), value: s.value }))} />`
        : null}

      ${kpis.length
        ? html`<div class="aq-row">
            ${kpis.map((k) => html`<${KPI} key=${k.key} label=${k.label} value=${k.value} />`)}
          </div>`
        : null}

      ${trendPoints.length > 1
        ? html`<${Section} title=${`${firstItem ? firstItem.label(trendKey) : trendKey} across submissions`}>
            <${TrendChart} points=${trendPoints} />
          <//>`
        : null}

      ${count
        ? html`<${Section} title="Submissions">
            <${RepeatedTable} entries=${rows} columns=${cols} d=${firstItem} />
          <//>`
        : html`<${Section} title="Submissions">
            <p class="aq-state-p">No submissions match this filter.</p>
          <//>`}

      ${center
        ? html`<${Section} title=${`Locations (${geo.length})`}>
            <${Map} lat=${center.lat} lng=${center.lng} zoom=${geo.length > 1 ? 4 : 13}
              name=${`${geo.length} location${geo.length === 1 ? '' : 's'}`} />
          <//>`
        : null}

      ${labs.length
        ? html`<${Section} title="Lab results"><${LabTable} rows=${labs} /><//>`
        : null}
    </div>`;
  };
}

// ---- helpers --------------------------------------------------------------
const SKIP_TYPES = [
  'image', 'drawing', 'voice', 'video', 'document', 'file', 'signature',
  'repeatedform', 'repeated', 'attachment'
];

function pickColumns(subs, numKeys) {
  if (numKeys.length) return numKeys;
  const first = subs[0];
  if (!first) return [];
  const out = [];
  for (const f of first.fields()) {
    if (!f || typeof f.fieldKey !== 'string') continue;
    if (SKIP_TYPES.includes(String(f.fieldType || '').toLowerCase())) continue;
    const v = f.value;
    if (Array.isArray(v) && v.length && typeof v[0] === 'object') continue;
    out.push(f.fieldKey);
    if (out.length >= 5) break;
  }
  return out;
}

function centroid(points) {
  if (!Array.isArray(points) || points.length === 0) return null;
  let lat = 0;
  let lng = 0;
  for (const p of points) {
    lat += p.lat;
    lng += p.lng;
  }
  return { lat: lat / points.length, lng: lng / points.length };
}

function roundish(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return null;
  const v = Number(n);
  return Math.abs(v) >= 100 ? Math.round(v) : Math.round(v * 100) / 100;
}

// Count occurrences of each value in a column → [{label, value}] sorted desc, top 6. Empty values bucket
// under "Unspecified". Used for the neutral status-distribution strip.
function countBy(arr) {
  const m = new Map();
  for (const raw of Array.isArray(arr) ? arr : []) {
    const k = raw == null || raw === '' ? 'Unspecified' : String(raw);
    m.set(k, (m.get(k) || 0) + 1);
  }
  return [...m.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 6);
}

function humanize(s) {
  const str = String(s == null ? '' : s);
  if (!str) return str;
  return str.replace(/[_-]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());
}

function formatDay(iso) {
  try {
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return String(iso);
    return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(iso);
  }
}

export default createAutoListView;
